// Phone timeline: the file's notes, calls, emails, SMS, P2Ps and system lines as a feed, newest first.
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { getAgeingTimeline } from "@/lib/api";
import { CATEGORY_META, CATEGORY_ORDER, getEventVisual, relativeTime, deskDayKey } from "@/pages/collections/ContactTimelineView";
import { PullToRefresh } from "@/components/phone/PhoneBits";
import { cn } from "@/lib/utils";

const dayLabel = (day) => {
  const today = deskDayKey(new Date().toISOString());
  const yesterday = deskDayKey(new Date(Date.now() - 86400000).toISOString());
  if (day === today) return "Today";
  if (day === yesterday) return "Yesterday";
  return new Date(`${day}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};

function EventCard({ event }) {
  const [open, setOpen] = useState(false);
  const meta = getEventVisual(event) || { icon: StickyNote, tone: "text-slate-600 bg-slate-100" };
  const Icon = meta.icon;
  const body = event.body || event.summary || "";
  const hasMore = body.length > 140 || (body.match(/\n/g) || []).length > 2;
  return (
    <div className="px-3 py-2.5 flex items-start gap-3" onClick={() => hasMore && setOpen((v) => !v)} data-testid={`phone-event-${event.type}`}>
      <div className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0", meta.tone)}><Icon className="w-4 h-4" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-[13px] font-semibold text-slate-800 leading-snug flex-1 min-w-0">{event.title}</span>
          <span className="text-[11px] text-slate-500 tabular-nums shrink-0" title={event.at}>{relativeTime(event.at)}</span>
        </div>
        {event.actor && <div className="text-[11px] text-slate-500">{event.actor}</div>}
        {body && <div className={cn("mt-1 text-[13px] text-slate-700 whitespace-pre-wrap break-words leading-snug", !open && "line-clamp-3")}>{body}</div>}
        {hasMore && <button type="button" className="mt-1 text-[11px] text-primary inline-flex items-center gap-0.5 h-7 sf-tap" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>{open ? "Show less" : "Show more"} <ChevronDown className={cn("w-3 h-3 transition-transform", open && "rotate-180")} /></button>}
      </div>
    </div>
  );
}

export function PhoneTimelineFeed({ caseRow, refreshKey = 0, isLegalMode = false }) {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(() => new Set(isLegalMode ? ["system", "payment"] : []));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getAgeingTimeline(caseRow.case_id, { limit: 300 });
      setEvents(r.events || []);
      setCounts(r.counts_by_category || {});
    } catch (e) { toast.error(`Timeline failed: ${e.response?.data?.detail || e.message}`); } finally { setLoading(false); }
  }, [caseRow.case_id]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const grouped = useMemo(() => {
    const out = [];
    let last = null;
    for (const e of events) {
      if (hidden.has(e.category)) continue;
      const day = deskDayKey(e.at);
      if (day !== last) { out.push({ kind: "day", day }); last = day; }
      out.push({ kind: "event", event: e });
    }
    return out;
  }, [events, hidden]);
  const cats = CATEGORY_ORDER.filter((c) => (counts[c] || 0) > 0);

  return (
    <div data-testid="phone-timeline">
      {cats.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto sf-no-scrollbar px-3 py-2 border-b bg-white sticky z-10" style={{ top: "var(--sf-file-header-bottom, 0px)" }} data-testid="phone-timeline-chips">
          {cats.map((c) => {
            const off = hidden.has(c);
            return (
              <button key={c} type="button" onClick={() => setHidden((prev) => { const n = new Set(prev); if (n.has(c)) n.delete(c); else n.add(c); return n; })}
                className={cn("h-8 px-2.5 rounded-full border text-[12px] font-medium whitespace-nowrap sf-tap", off ? "bg-white text-slate-400 border-slate-200 line-through" : CATEGORY_META[c].chipTone)}
                data-testid={`phone-timeline-chip-${c}`}>
                {CATEGORY_META[c].label} <span className="tabular-nums opacity-70">{counts[c]}</span>
              </button>
            );
          })}
        </div>
      )}
      <PullToRefresh onRefresh={load}>
        {loading && events.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading the timeline…</div>
        ) : grouped.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500" data-testid="phone-timeline-empty">Nothing on the timeline yet.</div>
        ) : (
          <div className="divide-y divide-slate-100 bg-white">
            {grouped.map((g, i) => g.kind === "day"
              ? <div key={`d-${g.day}-${i}`} className="px-3 py-1.5 bg-slate-50 text-[11px] font-semibold text-slate-600 uppercase tracking-wider" data-testid="phone-timeline-day">{dayLabel(g.day)}</div>
              : <EventCard key={g.event.id || `${g.event.type}-${g.event.at}-${i}`} event={g.event} />)}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
}
